// Global.js
import React, { useState, useEffect, useRef } from 'react';
import API from '../api';

function Global() {
  const [allowedDeps, setAllowedDeps] = useState([]);
  const [selectedDeps, setSelectedDeps] = useState([]);
  const [selectedPostalCodes, setSelectedPostalCodes] = useState([]);
  const [selectedNafCodes, setSelectedNafCodes] = useState([]);
  const [showPostalCodesModal, setShowPostalCodesModal] = useState(false);
  const [showNafCodesModal, setShowNafCodesModal] = useState(false);
  const [postalCodesData, setPostalCodesData] = useState({});
  const [nafCodesData, setNafCodesData] = useState([]);
  const [filteredPostalCodes, setFilteredPostalCodes] = useState([]);
  const [searchPostalCode, setSearchPostalCode] = useState('');
  const [searchNafCode, setSearchNafCode] = useState('');
  const [credits, setCredits] = useState(0);
  const [extractionStatus, setExtractionStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [downloadUrl, setDownloadUrl] = useState('');
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [statusCheckInterval, setStatusCheckInterval] = useState(null);
  const postalCodesRef = useRef(null);
  const nafCodesRef = useRef(null);
  const [currentDep, setCurrentDep] = useState(null);
  const [currentDepPostalCodes, setCurrentDepPostalCodes] = useState([]);
  const [temporaryPostalCodeSelection, setTemporaryPostalCodeSelection] = useState([]);
  const [temporaryNafCodeSelection, setTemporaryNafCodeSelection] = useState([]);
  const [extractionHistory, setExtractionHistory] = useState([]);
  const [licenseStatus, setLicenseStatus] = useState(""); // Ajout du statut de la licence

  useEffect(() => {
    Promise.all([
      API.get('/api/auth/user-info'),
      fetch('/codes_postaux.json').then(res => res.json()),
      fetch('/codes_naf.json').then(res => res.json())
    ])
      .then(([userRes, postalCodes, nafCodes]) => {
        const user = userRes.data;
        if (user.departementsAutorises && user.departementsAutorises.length > 0) {
          const formattedDeps = user.departementsAutorises.map(dep => dep.toString().padStart(2, '0'));
          setAllowedDeps(formattedDeps);
        }
        setCredits(user.credits || 0);
        setPostalCodesData(postalCodes);
        setNafCodesData(nafCodes.filter(item => item["Code NAF"]));
        setLicenseStatus(user.licenseStatus || ""); // Stocker le statut de la licence
      })
      .catch((err) => {
        console.error("Erreur lors du chargement des données:", err);
        setError("Erreur lors du chargement des données utilisateur ou des codes.");
      });

    loadExtractionHistory();
  }, []);

  const loadExtractionHistory = async () => {
    try {
      const response = await API.get('/api/extraction/history/global');
      setExtractionHistory(response.data);
    } catch (err) {
      console.error("Erreur lors du chargement de l'historique des extractions:", err);
    }
  };

  useEffect(() => {
    if (Object.keys(postalCodesData).length > 0 && selectedDeps.length > 0) {
      let allPostalCodes = [];
      
      selectedDeps.forEach(dep => {
        if (postalCodesData[dep]) {
          Object.keys(postalCodesData[dep]).forEach(postalCode => {
            if (postalCodesData[dep][postalCode] && postalCodesData[dep][postalCode].length > 0) {
              allPostalCodes.push({
                code: postalCode,
                communes: postalCodesData[dep][postalCode],
                departement: dep
              });
            }
          });
        }
      });
      
      setFilteredPostalCodes(allPostalCodes);
    } else {
      setFilteredPostalCodes([]);
    }
  }, [postalCodesData, selectedDeps]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (postalCodesRef.current && !postalCodesRef.current.contains(event.target)) {
        setShowPostalCodesModal(false);
      }
      if (nafCodesRef.current && !nafCodesRef.current.contains(event.target)) {
        setShowNafCodesModal(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (extractionStatus === 'loading') {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
      
      const interval = setInterval(() => {
        checkFileStatus();
      }, 1000); // Changé de 3000 à 1000 pour rafraîchir chaque seconde
      
      setStatusCheckInterval(interval);
      checkFileStatus();
    } else {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
        setStatusCheckInterval(null);
      }
    }
    
    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [extractionStatus, result]);

  // Limitation pour la licence Demo
  const isDemo = licenseStatus === "Demo";
  const demoPostalSelected = selectedPostalCodes.length > 0;
  const demoNafSelected = selectedNafCodes.length > 0;

  const handleDepChange = (e) => {
    const dep = e.target.value;
    if (e.target.checked) {
      setSelectedDeps([...selectedDeps, dep]);
    } else {
      setSelectedDeps(selectedDeps.filter(item => item !== dep));
      setSelectedPostalCodes(selectedPostalCodes.filter(pc => !pc.startsWith(dep)));
    }
  };

  const handlePostalCodeChange = (e, postalCode) => {
    if (isDemo && !demoPostalSelected) {
      setSelectedPostalCodes([postalCode]);
    } else if (isDemo && demoPostalSelected) {
      // Ne rien faire si déjà sélectionné (empêche la sélection d'autres)
      return;
    } else {
      if (e.target.checked) {
        setSelectedPostalCodes([...selectedPostalCodes, postalCode]);
      } else {
        setSelectedPostalCodes(selectedPostalCodes.filter(code => code !== postalCode));
      }
    }
  };

  const handleNafCodeChange = (e, nafCode) => {
    if (isDemo && !demoNafSelected) {
      setSelectedNafCodes([nafCode]);
    } else if (isDemo && demoNafSelected) {
      // Ne rien faire si déjà sélectionné (empêche la sélection d'autres)
      return;
    } else {
      if (e.target.checked) {
        setSelectedNafCodes([...selectedNafCodes, nafCode]);
      } else {
        setSelectedNafCodes(selectedNafCodes.filter(code => code !== nafCode));
      }
    }
  };

  const filterPostalCodes = () => {
    if (!searchPostalCode) return filteredPostalCodes;
    
    return filteredPostalCodes.filter(item => 
      item.code.includes(searchPostalCode) || 
      item.communes.some(commune => commune.toLowerCase().includes(searchPostalCode.toLowerCase()))
    );
  };

  const filterNafCodes = () => {
    if (!searchNafCode || !nafCodesData) return nafCodesData || [];
    
    return nafCodesData.filter(item => 
      item["Code NAF"]?.includes(searchNafCode) || 
      item.Description?.toLowerCase().includes(searchNafCode.toLowerCase())
    );
  };

  const checkFileStatus = async () => {
    if (!result || !result.job_id) return;
  
    try {
      const response = await API.get(`/api/extraction/status/${result.job_id}`);
  
      // progression lissée de 1% en 1%
      setExtractionProgress(prev => {
        if (response.data.status === 'complete') {
          return 100;
        }
        const serverP = Math.min(response.data.progress || 0, 99);
        return prev >= serverP ? Math.min(prev + 1, 99) : serverP;
      });
  
      if (response.data.status === 'complete') {
        setExtractionStatus('complete');
        if (response.data.download_url) {
          setDownloadUrl(response.data.download_url);
          await refreshKPI();
        } else {
          setError("URL de téléchargement non disponible. Veuillez contacter le support.");
        }
      } else if (response.data.status === 'no_data') {
        setExtractionStatus('idle');
        setError("Aucune donnée trouvée pour les critères sélectionnés.");
      } else if (response.data.status === 'failed') {
        setError(response.data.error || "Erreur lors de l'extraction. Veuillez réessayer.");
        setExtractionStatus('idle');
      }
  
      if (response.data.download_url && extractionStatus !== 'complete') {
        setDownloadUrl(response.data.download_url);
      }
    } catch (err) {
      console.error("Erreur lors de la vérification de l'état du fichier:", err);
    }
  };

  const refreshKPI = async () => {
    try {
      const response = await API.get('/api/ia/refresh-kpi');
      console.log("KPI refreshed successfully:", response.data);
    } catch (error) {
      console.error("Erreur lors de la mise à jour des KPI:", error);
    }
  };

  const handleExtraction = async () => {
    if (selectedDeps.length === 0) {
      setError("Veuillez sélectionner au moins un département.");
      return;
    }
    
    // ENRICHISSEMENT DÉSACTIVÉ TEMPORAIREMENT
    // if (enrich && !handleEnrichmentWarnings()) return;
    
    if (extractionStatus === 'loading') {
      setError("Une extraction est déjà en cours. Veuillez patienter.");
      return;
    }
    
    setError("");
    setExtractionStatus('loading');
    setDownloadUrl('');
    setExtractionProgress(0);
    
    try {
      const response = await API.post('/api/extraction/global', {
        postal_codes: selectedPostalCodes,
        codes_naf: selectedNafCodes,
      });
      setResult(response.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 403 && detail?.includes("Limite démo")) {
        setError(detail);
      } else {
        setError("Erreur lors de l'extraction G.L.O.B.A.L.");
      }
      setExtractionStatus('idle');
    }
  };

  const handleDownload = async () => {
    if (!result || !result.job_id) {
      setError("Identifiant de tâche non disponible. Veuillez réessayer.");
      return;
    }
    
    try {
      const response = await API.get(`/api/extraction/download/${result.job_id}`, {
        responseType: 'blob',
      });
      
      if (!response.data || response.data.size === 0) {
        throw new Error("Le fichier téléchargé est vide");
      }
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      
      let fileName = `GLOBAL_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
      
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      setError("Erreur lors du téléchargement du fichier. Veuillez réessayer.");
    }
  };

  const downloadHistoryFile = async (jobId) => {
    try {
      const response = await API.get(`/api/extraction/download/${jobId}`, {
        responseType: 'blob',
      });
      
      if (!response.data || !response.data.size === 0) {
        throw new Error("Le fichier téléchargé est vide");
      }
      
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      
      let fileName = `GLOBAL_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length > 1) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Erreur lors du téléchargement du fichier historique:", error);
      setError("Erreur lors du téléchargement du fichier historique. Veuillez réessayer.");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR');
  };

  const isLinkExpired = (timestamp) => {
    if (!timestamp) return true;
    const extractionDate = new Date(timestamp);
    const currentDate = new Date();
    const diffHours = (currentDate - extractionDate) / (1000 * 60 * 60);
    return diffHours > 24;
  };

  const openPostalCodesForDepartment = (dep) => {
    if (!postalCodesData[dep]) return;
    
    let depPostalCodes = [];
    Object.keys(postalCodesData[dep]).forEach(postalCode => {
      if (postalCodesData[dep][postalCode] && postalCodesData[dep][postalCode].length > 0) {
        depPostalCodes.push({
          code: postalCode,
          communes: postalCodesData[dep][postalCode],
          departement: dep
        });
      }
    });
    
    setCurrentDep(dep);
    setCurrentDepPostalCodes(depPostalCodes);
    const depSelectedCodes = selectedPostalCodes.filter(code => code.startsWith(dep));
    setTemporaryPostalCodeSelection(depSelectedCodes);
    setSearchPostalCode('');
    setShowPostalCodesModal(true);
  };

  const openNafCodesModal = (e) => {
    if (e) e.preventDefault();
    setTemporaryNafCodeSelection([...selectedNafCodes]);
    setSearchNafCode('');
    setShowNafCodesModal(true);
  };

  const filterCurrentDepPostalCodes = () => {
    if (!searchPostalCode) return currentDepPostalCodes;
    
    return currentDepPostalCodes.filter(item => 
      item.code.includes(searchPostalCode) || 
      item.communes.some(commune => commune.toLowerCase().includes(searchPostalCode.toLowerCase()))
    );
  };

  const handleTemporaryPostalCodeChange = (code, isChecked) => {
    if (isDemo) {
      if (!demoPostalSelected && isChecked) {
        setTemporaryPostalCodeSelection([code]);
      } else if (demoPostalSelected && !isChecked) {
        setTemporaryPostalCodeSelection([]);
      }
      // Ne rien faire si déjà sélectionné
    } else {
      if (isChecked) {
        setTemporaryPostalCodeSelection([...temporaryPostalCodeSelection, code]);
      } else {
        setTemporaryPostalCodeSelection(temporaryPostalCodeSelection.filter(c => c !== code));
      }
    }
  };

  const handleTemporaryNafCodeChange = (code, isChecked) => {
    if (isDemo) {
      if (!demoNafSelected && isChecked) {
        setTemporaryNafCodeSelection([code]);
      } else if (demoNafSelected && !isChecked) {
        setTemporaryNafCodeSelection([]);
      }
      // Ne rien faire si déjà sélectionné
    } else {
      if (isChecked) {
        setTemporaryNafCodeSelection([...temporaryNafCodeSelection, code]);
      } else {
        setTemporaryNafCodeSelection(temporaryNafCodeSelection.filter(c => c !== code));
      }
    }
  };

  const validatePostalCodeSelection = () => {
    const otherDepsSelected = selectedPostalCodes.filter(code => !code.startsWith(currentDep));
    setSelectedPostalCodes([...otherDepsSelected, ...temporaryPostalCodeSelection]);
    if (temporaryPostalCodeSelection.length > 0 && !selectedDeps.includes(currentDep)) {
      setSelectedDeps([...selectedDeps, currentDep]);
    } else if (temporaryPostalCodeSelection.length === 0 && selectedDeps.includes(currentDep)) {
      setSelectedDeps(selectedDeps.filter(d => d !== currentDep));
    }
    setShowPostalCodesModal(false);
  };

  const validateNafCodeSelection = () => {
    setSelectedNafCodes([...temporaryNafCodeSelection]);
    setShowNafCodesModal(false);
  };

  const selectAllFilteredPostalCodesInModal = () => {
    const filteredCodes = filterCurrentDepPostalCodes().map(item => item.code);
    const newSelection = [...new Set([...temporaryPostalCodeSelection, ...filteredCodes])];
    setTemporaryPostalCodeSelection(newSelection);
  };

  const selectAllFilteredNafCodesInModal = () => {
    const filteredCodes = filterNafCodes().map(item => item["Code NAF"]);
    const newSelection = [...new Set([...temporaryNafCodeSelection, ...filteredCodes])];
    setTemporaryNafCodeSelection(newSelection);
  };

  return (
    <div className="global-container">
      <h2>Extraction G.L.O.B.A.L</h2>
      {/* Message pour la licence Demo */}
      {isDemo && (
        <div className="demo-warning" style={{
          background: "#fff3cd",
          color: "#856404",
          border: "1px solid #ffeeba",
          borderRadius: "5px",
          padding: "15px",
          marginBottom: "20px"
        }}>
          <strong>Mode démo activé :</strong> Vous ne pouvez accéder qu'à des fonctionalités limitées.<br />
          Pour débloquer toutes les fonctionnalités, <a href="https://www.leadxplore.fr/subscribe.html" target="_blank" rel="noopener noreferrer">rendez-vous sur la page de souscription</a>.
        </div>
      )}
      {error && <p className="error">{error}</p>}
      <div className="form-group">
        <label>Sélectionnez les codes postaux :</label>
        <div className="department-buttons">
          {allowedDeps.length > 0 ? (
            allowedDeps.map((dep) => (
              <button
                key={dep}
                className={`dep-button ${selectedDeps.includes(dep) ? 'selected' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  openPostalCodesForDepartment(dep);
                }}
                disabled={extractionStatus === 'loading'}
                style={{ width: '80px', margin: '5px' }}
              >
                {dep}
              </button>
            ))
          ) : (
            <p>Aucun département autorisé disponible.</p>
          )}
        </div>
        
        <div className="selected-codes-summary">
          {selectedPostalCodes.length > 0 ? (
            <div className="selected-postal-codes">
              <p>
                <strong>{selectedPostalCodes.length}</strong> codes postaux sélectionnés : 
                {selectedPostalCodes.length > 10 
                  ? ` ${selectedPostalCodes.slice(0, 10).join(', ')}...` 
                  : ` ${selectedPostalCodes.join(', ')}`}
              </p>
            </div>
          ) : (
            <p>Aucun code postal sélectionné</p>
          )}
        </div>
      </div>
      
      <div className="form-group">
        <label>Sélectionnez les codes NAF :</label>
        <button 
          className="naf-button"
          onClick={(e) => openNafCodesModal(e)}
          disabled={extractionStatus === 'loading'}
          style={{ width: 'auto', display: 'inline-block', padding: '8px 20px' }}
          type="button"
        >
          Codes NAF
        </button>
        
        <div className="selected-codes-summary">
          {selectedNafCodes.length > 0 ? (
            <div className="selected-naf-codes">
              <p>
                <strong>{selectedNafCodes.length}</strong> codes NAF sélectionnés : 
                {selectedNafCodes.length > 10 
                  ? ` ${selectedNafCodes.slice(0, 10).join(', ')}...` 
                  : ` ${selectedNafCodes.join(', ')}`}
              </p>
            </div>
          ) : (
            <p>Aucun code NAF sélectionné</p>
          )}
        </div>
      </div>
      
      {showPostalCodesModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content modern-modal" ref={postalCodesRef} style={{ 
            width: '90%', 
            maxWidth: '700px',
            maxHeight: '80vh', 
            display: 'flex', 
            flexDirection: 'column',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
            padding: '20px',
            position: 'relative'
          }}>
            <h3>Codes postaux - Département {currentDep}</h3>
            <div className="modal-search" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10, padding: '10px 0' }}>
              <input 
                type="text" 
                placeholder="Rechercher un code postal ou une commune..."
                value={searchPostalCode}
                onChange={(e) => setSearchPostalCode(e.target.value)}
                style={{ width: '100%', marginBottom: '10px' }}
              />
              <button 
                onClick={selectAllFilteredPostalCodesInModal}
                className="select-all-button"
                type="button"
              >
                Tous
              </button>
            </div>
            <div className="modal-list scrollable-list" style={{ overflowY: 'auto', flex: 1, maxHeight: '50vh', border: '1px solid #eee', padding: '10px', borderRadius: '4px' }}>
              {filterCurrentDepPostalCodes().length > 0 ? filterCurrentDepPostalCodes().map((item) => {
                // Pour la licence Demo, griser les autres codes si un est sélectionné
                const isChecked = temporaryPostalCodeSelection.includes(item.code);
                const isDisabled = isDemo && !isChecked && temporaryPostalCodeSelection.length > 0;
                return (
                  <label key={item.code} className={`checkbox-label${isDisabled ? ' disabled' : ''}`} style={isDisabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={(e) => handleTemporaryPostalCodeChange(item.code, e.target.checked)}
                    />
                    <span className="code">{item.code}</span> - <span className="commune">{item.communes.join(', ')}</span>
                  </label>
                );
              }) : <p>Aucun résultat</p>}
            </div>
            <div className="modal-actions" style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '10px 0' }}>
              <button className="cancel-button" onClick={() => setShowPostalCodesModal(false)} type="button">Annuler</button>
              <button className="validate-button" onClick={validatePostalCodeSelection} type="button">Valider</button>
            </div>
          </div>
        </div>
      )}
      
      {showNafCodesModal && (
        <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div className="modal-content modern-modal" ref={nafCodesRef} style={{ 
            width: '90%', 
            maxWidth: '700px',
            maxHeight: '80vh', 
            display: 'flex', 
            flexDirection: 'column',
            background: 'white',
            borderRadius: '8px',
            boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
            padding: '20px',
            position: 'relative'
          }}>
            <h3>Sélection des codes NAF</h3>
            <div className="modal-search" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10, padding: '10px 0' }}>
              <input 
                type="text" 
                placeholder="Rechercher un code NAF ou une activité..."
                value={searchNafCode}
                onChange={(e) => setSearchNafCode(e.target.value)}
                style={{ width: '100%', marginBottom: '10px' }}
              />
              <button 
                onClick={selectAllFilteredNafCodesInModal}
                className="select-all-button"
                type="button"
              >
                Tous
              </button>
            </div>
            <div className="modal-list scrollable-list" style={{ overflowY: 'auto', flex: 1, maxHeight: '50vh', border: '1px solid #eee', padding: '10px', borderRadius: '4px' }}>
              {Array.isArray(filterNafCodes()) && filterNafCodes().length > 0 ? filterNafCodes().map((item) => {
                const code = item["Code NAF"];
                const isChecked = temporaryNafCodeSelection.includes(code);
                const isDisabled = isDemo && !isChecked && temporaryNafCodeSelection.length > 0;
                return (
                  <label key={code} className={`checkbox-label${isDisabled ? ' disabled' : ''}`} style={isDisabled ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      disabled={isDisabled}
                      onChange={(e) => handleTemporaryNafCodeChange(code, e.target.checked)}
                    />
                    <span className="code">{code}</span> - <span className="label">{item.Description}</span>
                  </label>
                );
              }) : <p>Aucun résultat</p>}
            </div>
            <div className="modal-actions" style={{ marginTop: 'auto', display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '10px 0' }}>
              <button className="cancel-button" onClick={() => setShowNafCodesModal(false)} type="button">Annuler</button>
              <button className="validate-button" onClick={validateNafCodeSelection} type="button">Valider</button>
            </div>
          </div>
        </div>
      )}

            {/* Ajout de la case à cocher enrichissement */}
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            disabled
            style={{ marginRight: 8 }}
          />
          Activer l'enrichissement
          {licenseStatus === "Demo" && (
            <span style={{ color: "#888", marginLeft: 8 }}>(désactivé en mode demo)</span>
          )}
        </label>
      </div>

      
      <div className="extraction-controls">
        <button 
          className="action-button" 
          onClick={handleExtraction}
          disabled={extractionStatus === 'loading'}
        >
          {extractionStatus === 'loading' ? 'Extraction en cours...' : 'Lancer l\'extraction'}
        </button>
        
        {extractionStatus === 'loading' && (
          <div className="timer">
            <p>Progression : {Math.round(extractionProgress)}%</p>
            <div className="progress-container">
              <div 
                className="progress-bar" 
                style={{ width: `${extractionProgress}%` }}
              >
                <span className="progress-text">{Math.round(extractionProgress)}%</span>
              </div>
            </div>
          </div>
        )}
        
        {extractionStatus === 'complete' && downloadUrl && (
          <button 
            className="download-button" 
            onClick={handleDownload}
          >
            Télécharger le fichier
          </button>
        )}
      </div>
      
      <div className="extraction-history">
        <h3>Historique des extractions G.L.O.B.A.L (liens valides 24h!)</h3>
        {extractionHistory.length > 0 ? (
          <table className="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Codes postaux</th>
                <th>Codes NAF</th>
                <th>Nombre de leads</th>
                <th>Téléchargement</th>
              </tr>
            </thead>
            <tbody>
              {extractionHistory.map((item, index) => (
                <tr key={index}>
                  <td>{formatDate(item.timestamp)}</td>
                  <td>{Array.isArray(item.deps_or_codes) && item.deps_or_codes.length > 0 
                    ? item.deps_or_codes.slice(0, 5).join(', ') + (item.deps_or_codes.length > 5 ? '...' : '') 
                    : 'N/A'}</td>
                  <td>{Array.isArray(item.codes_naf) && item.codes_naf.length > 0 
                    ? item.codes_naf.slice(0, 5).join(', ') + (item.codes_naf.length > 5 ? '...' : '') 
                    : 'N/A'}</td>
                  <td>{item.count || 0}</td>
                  <td>
                      {item.job_id ? (
                        isLinkExpired(item.timestamp) ? (
                          <button 
                            className="download-button-small expired" 
                            disabled
                          >
                            Lien expiré
                          </button>
                        ) : (
                          <button 
                            className="download-button-small" 
                            onClick={() => downloadHistoryFile(item.job_id)}
                          >
                            Télécharger
                          </button>
                        )
                      ) : (
                        "Non disponible"
                      )}
                    </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>Aucun historique d'extraction disponible.</p>
        )}
      </div>
    </div>
  );
}

export default Global;
